import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

// Image marquees move at a steady pace in px per second, however many images they hold.
const MEDIA_SPEED = 60;

// Pointer travel before a press on an image marquee counts as a drag instead of a click.
const DRAG_THRESHOLD = 6;

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  async connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    const { numberOfCopies } = await this.#queryNumberOfCopies();

    this.#addRepeatedItems(numberOfCopies);
    this.#duplicateContent();

    this.#setSpeed(this.#isMedia ? this.#calculateMediaSpeed() : this.#calculateSpeed(numberOfCopies));

    window.addEventListener('resize', this.#handleResize);

    // Image marquees keep moving and can be dragged; text marquees slow down on hover.
    this.#dragController = new AbortController();
    const { signal } = this.#dragController;

    if (this.#isMedia) {
      this.addEventListener('pointerdown', this.#handlePointerDown, { signal });
      this.addEventListener('pointermove', this.#handlePointerMove, { signal });
      this.addEventListener('pointerup', this.#handlePointerUp, { signal });
      this.addEventListener('pointercancel', this.#handlePointerUp, { signal });
      this.addEventListener('click', this.#handleClick, { capture: true, signal });
      this.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    } else {
      this.addEventListener('pointerenter', this.#slowDown, { signal });
      this.addEventListener('pointerleave', this.#speedUp, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.#dragController?.abort();
  }

  get #isMedia() {
    return this.classList.contains('marquee--media');
  }

  /**
   * @type {AbortController | null}
   */
  #dragController = null;

  /**
   * @type {{ startX: number, lastX: number, moved: boolean, animation: Animation } | null}
   */
  #drag = null;

  #suppressClick = false;

  /**
   * @param {PointerEvent} event
   */
  #handlePointerDown = (event) => {
    this.#suppressClick = false;
    if (!event.isPrimary || event.button !== 0) return;

    // Without an animation (reduced motion) the strip scrolls natively instead.
    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation) return;

    this.#drag = { startX: event.clientX, lastX: event.clientX, moved: false, animation };
  };

  /**
   * @param {PointerEvent} event
   */
  #handlePointerMove = (event) => {
    const drag = this.#drag;
    if (!drag) return;

    if (!drag.moved) {
      if (Math.abs(event.clientX - drag.startX) < DRAG_THRESHOLD) return;

      drag.moved = true;
      drag.animation.pause();
      this.setPointerCapture(event.pointerId);
      this.setAttribute('dragging', '');
    }

    this.#scrub(drag.animation, event.clientX - drag.lastX);
    drag.lastX = event.clientX;
  };

  #handlePointerUp = () => {
    const drag = this.#drag;
    this.#drag = null;
    if (!drag?.moved) return;

    this.removeAttribute('dragging');
    drag.animation.play();
    // A mouse drag that ends over an image would otherwise open its link.
    this.#suppressClick = true;
  };

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (!this.#suppressClick) return;

    this.#suppressClick = false;
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * Moves the animation by a pointer distance so the strip follows the finger or cursor.
   * @param {Animation} animation
   * @param {number} deltaX - Horizontal pointer movement in px.
   */
  #scrub(animation, deltaX) {
    const timing = animation.effect?.getComputedTiming();
    const duration = Number(timing?.duration);
    const distance = this.#loopDistance;
    if (!duration || !distance) return;

    // The keyframes move content towards the start, so dragging towards the end rewinds them.
    const sign = timing?.direction === 'reverse' ? 1 : -1;
    const time = Number(animation.currentTime ?? 0) + (sign * deltaX * duration) / distance;

    animation.currentTime = ((time % duration) + duration) % duration;
  }

  /**
   * Distance the strip travels in one loop: one copy of the content plus the gap after it.
   */
  get #loopDistance() {
    const { content, wrapper } = this.refs;
    return content.offsetWidth + (parseFloat(getComputedStyle(wrapper).columnGap) || 0);
  }

  #calculateMediaSpeed() {
    return this.#loopDistance / MEDIA_SPEED;
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  /**
   * @type {number | null}
   */
  #marqueeWidth = null;

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    return new Promise((resolve) => {
      if (!marqueeItems[0]) {
        // Wrapping the resolve in a setTimeout here and below splits each marquee reflow into a separate task.
        return setTimeout(() => resolve({ numberOfCopies: 1, isHorizontalResize: true }), 0);
      }

      const intersectionObserver = new IntersectionObserver(
        (entries) => {
          const firstEntry = entries[0];
          if (!firstEntry) return;
          intersectionObserver.disconnect();

          const { width: marqueeWidth } = firstEntry.rootBounds ?? { width: 0 };
          const { width: marqueeItemsWidth } = firstEntry.boundingClientRect;

          const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
          this.#marqueeWidth = marqueeWidth;

          setTimeout(() => {
            resolve({
              numberOfCopies: marqueeItemsWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeItemsWidth),
              isHorizontalResize,
            });
          }, 0);
        },
        { root: this }
      );
      intersectionObserver.observe(marqueeItems[0]);
    });
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const speed = Math.sqrt(numberOfCopies) * speedFactor;

    return speed;
  }

  #handleResize = debounce(async () => {
    const { marqueeItems } = this.refs;
    const { newNumberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();

    // opt out of marquee manipulation on vertical resizes
    if (!isHorizontalResize) return;

    const currentNumberOfCopies = marqueeItems.length;
    const speed = this.#isMedia ? this.#calculateMediaSpeed() : this.#calculateSpeed(newNumberOfCopies);

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();
    this.#setSpeed(speed);
    this.#restartAnimation();
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    // The copy only exists for the seamless loop, so keep its links out of the tab order too.
    clone.setAttribute('aria-hidden', 'true');
    clone.inert = true;
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} numberOfCopies
   */
  #addRepeatedItems(numberOfCopies) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = /** @type {HTMLElement} */ (marqueeItems[0].cloneNode(true));
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      content.appendChild(clone);
    }
  }

  /**
   * @param {number} numberOfCopies
   */
  #removeRepeatedItems(numberOfCopies) {
    const { content } = this.refs;
    const children = Array.from(content.children);

    const itemsToRemove = Math.min(numberOfCopies, children.length - 1);

    for (let i = 0; i < itemsToRemove; i++) {
      content.lastElementChild?.remove();
    }
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
